import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const EMPTY = { brandName: "", brandCode: "" };

export default function Brands() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async (q = "") => {
    const { data } = await api.get("/brands", { params: { search: q } });
    setBrands(data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const tm = setTimeout(() => load(search), 300); return () => clearTimeout(tm); }, [search]);

  const handleCreate = async () => {
    if (!form.brandName || !form.brandCode) { toast.error(t("brands.nameRequired")); return; }
    try {
      const { data } = await api.post("/brands", form);
      toast.success(t("brands.created"));
      setCreating(false);
      setForm(EMPTY);
      navigate(`/brands/${data.id}`);
    } catch (e) {
      toast.error(t("brands.createFailed"));
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-8 py-10" data-testid="brands-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-700 text-slate-900">{t("brands.title")}</h1>
        {isAdmin && (
          <Button className="bhf-btn-primary gap-1.5 border-0 text-white" onClick={() => setCreating(!creating)} data-testid="add-brand-button">
            <Plus className="h-4 w-4" /> {t("brands.add")}
          </Button>
        )}
      </div>
      <div className="mt-5 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder={t("brands.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="brand-search-input" />
      </div>

      {creating && (
        <div className="bhf-card mt-5 flex flex-wrap items-end gap-3 p-4" data-testid="brand-create-form">
          <div>
            <label className="text-xs text-slate-500">{t("brands.name")}</label>
            <Input className="mt-1" value={form.brandName} onChange={(e) => setForm({ ...form, brandName: e.target.value })} data-testid="new-brand-name-input" />
          </div>
          <div>
            <label className="text-xs text-slate-500">{t("brands.code")}</label>
            <Input className="mt-1" value={form.brandCode} onChange={(e) => setForm({ ...form, brandCode: e.target.value })} data-testid="new-brand-code-input" />
          </div>
          <Button className="bhf-btn-primary border-0 text-white" onClick={handleCreate} data-testid="create-brand-submit">{t("brands.createConfigure")}</Button>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {brands.map((b) => (
          <div key={b.id} className="bhf-card cursor-pointer overflow-hidden transition-shadow hover:shadow-lg" onClick={() => navigate(`/brands/${b.id}`)} data-testid={`brand-card-${b.id}`}>
            <div className="flex h-28 items-center justify-center" style={{ background: b.backgrounds?.default?.startsWith("#") ? b.backgrounds.default : "#F5F0E8" }}>
              {b.logos?.main ? <img src={fileUrl(b.logos.main)} alt={b.brandName} className="h-14 object-contain" /> : <span className="font-display text-lg font-700" style={{ color: b.colors?.primary }}>{b.brandName}</span>}
            </div>
            <div className="p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-800">{b.brandName}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${b.status === "Active" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{b.status === "Active" ? t("products.active") : t("products.inactive")}</span>
              </div>
              <div className="mt-0.5 font-mono-bhf text-[11px] text-slate-400">{b.brandCode}</div>
              <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                <span className="truncate">{b.fonts?.titleFont}</span>
                <div className="flex items-center gap-1.5">
                  <span className="h-4 w-4 rounded-full border border-slate-200" style={{ background: b.colors?.primary }} />
                  <span className="h-4 w-4 rounded-full border border-slate-200" style={{ background: b.colors?.secondary }} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
